import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, Button, IconButton, Tab, Tabs, TextField } from '@mui/material';
import UpAndDownFilterIcon from 'assets/images/iPrise/UpAndDownFilter.svg';
import { useDebounce } from 'hooks';
import { useStore } from 'mobx/StoreContext';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useState } from 'react';

import AddAndEditJobV2 from '../../../common/AddAndEditJobV2';
import TeamSelectMenu from '../../../common/components/TeamSelectMenu';
import SortMenu from './SortMenu';

const JobQueueHeader: React.FC = () => {
  const [openAddJob, setOpenAddJob] = useState(false);
  const [anchorElTeam, setAnchorElTeam] = useState<null | HTMLElement>(null);
  const [anchorElSort, setAnchorElSort] = useState<null | HTMLElement>(null);
  const { businessHeadJobQueueService } = useStore();
  const { selectedTeamName, selectedTeam, selectedSort, mainTab } =
    businessHeadJobQueueService;
  const [localSearch, setLocalSearch] = useState(
    businessHeadJobQueueService.searchTerm,
  );
  const debouncedSearch = useDebounce(localSearch, 500);

  const [localPercentage, setLocalPercentage] = useState(
    businessHeadJobQueueService.percentage,
  );
  const debouncedPercentage = useDebounce(localPercentage, 500);

  // Sync debounced search to MobX store
  useEffect(() => {
    businessHeadJobQueueService.setSearchTerm(debouncedSearch);
  }, [debouncedSearch, businessHeadJobQueueService]);

  // Sync debounced percentage to MobX store
  useEffect(() => {
    businessHeadJobQueueService.setPercentage(debouncedPercentage);
  }, [debouncedPercentage, businessHeadJobQueueService]);

  const handleTeamClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAnchorElTeam(event.currentTarget);
    },
    [],
  );

  const handleTeamClose = useCallback(() => {
    setAnchorElTeam(null);
  }, []);

  const handleTeamSelect = useCallback(
    (teamId: string, teamName: string) => {
      businessHeadJobQueueService.setSelectedTeam(teamId);
      businessHeadJobQueueService.setSelectedTeamName(teamName);
      setAnchorElTeam(null);
    },
    [businessHeadJobQueueService],
  );

  const handleSortClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAnchorElSort(event.currentTarget);
    },
    [],
  );

  const handleSortClose = useCallback(() => {
    setAnchorElSort(null);
  }, []);

  const handleSortSelect = useCallback(
    (sort: string) => {
      businessHeadJobQueueService.setSelectedSort(sort);
      setAnchorElSort(null);
    },
    [businessHeadJobQueueService],
  );

  const handleMainTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      businessHeadJobQueueService.setMainTab(newValue);
    },
    [businessHeadJobQueueService],
  );

  const handleSearchChange = useCallback((value: string) => {
    setLocalSearch(value);
  }, []);

  const handlePercentageChange = useCallback((value: string) => {
    setLocalPercentage(value);
  }, []);

  return (
    <Box className="main-tabs-container">
      <Tabs
        value={mainTab}
        onChange={handleMainTabChange}
        className="main-tabs"
        TabIndicatorProps={{
          className: 'tab-indicator',
        }}>
        <Tab label="Bench Queue" className="main-tab" />
        <Tab label="Job Queue" className="main-tab" />
      </Tabs>
      <Box className="header-actions">
        <TextField
          placeholder="Search"
          value={localSearch}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="search-field"
          size="small"
        />
        <Box className="team-select" onClick={handleTeamClick}>
          {selectedTeamName || 'All Teams'}
          <KeyboardArrowDownIcon sx={{ color: '#242424', fontSize: 20 }} />
        </Box>
        <TeamSelectMenu
          anchorEl={anchorElTeam}
          open={Boolean(anchorElTeam)}
          onClose={handleTeamClose}
          selectedTeam={selectedTeam}
          onSelect={handleTeamSelect}
        />
        <IconButton className="sort-button" onClick={handleSortClick}>
          <img src={UpAndDownFilterIcon} alt="Sort" />
        </IconButton>
        <SortMenu
          anchorEl={anchorElSort}
          open={Boolean(anchorElSort)}
          onClose={handleSortClose}
          selectedSort={selectedSort}
          onSelect={handleSortSelect}
        />
        <Box className="percentage-input-container">
          <TextField
            value={localPercentage}
            onChange={(e) => handlePercentageChange(e.target.value)}
            className="percentage-input"
            placeholder="0"
            size="small"
            InputProps={{
              endAdornment: (
                <Box
                  component="span"
                  sx={{ color: '#5E6C84', fontSize: '13px', width: '20px' }}>
                  %
                </Box>
              ),
            }}
          />
        </Box>
        <Button
          variant="contained"
          onClick={() => setOpenAddJob(true)}
          sx={{
            '&:hover': { bgcolor: '#374151', boxShadow: 'none' },
            bgcolor: '#111827',
            borderRadius: '4px',
            boxShadow: 'none',
            color: 'white',
            fontSize: '14px',
            fontWeight: 500,
            height: '32px',
            minWidth: '90px',
            ml: 2,
            textTransform: 'none',
          }}>
          + Add Job
        </Button>
      </Box>
      <AddAndEditJobV2
        open={openAddJob}
        onClose={() => setOpenAddJob(false)}
        scroll="paper"
        isBenchQueue={mainTab === 0}
      />
    </Box>
  );
};

export default observer(JobQueueHeader);
